<template>
  <div class="py-12 sm:py-16 md:py-20 lg:py-24 grid md:grid-cols-2 gap-8">
    <div>
      <THeading
        :level="3"
        tag="h2"
        class="mb-2"
      >
        {{ t('title') }}
      </THeading>

      <TBody
        size="lg"
        variant="muted"
        class="mb-8"
      >
        {{ t('content') }}
      </TBody>

      <THeading
        :level="5"
        tag="h3"
        class="mb-4"
      >
        {{ t('listYourRv.title') }}
      </THeading>

      <ul class="text-lg text-gray-500 space-y-3 max-w-md">
        <li
          v-for="item in (tm('listYourRv.items') as string[])"
          :key="item"
          class="flex"
        >
          <TIcon
            :icon="['fal', 'circle-check']"
            fixed-width
            class="text-success mr-2 shrink-0 mt-1.25"
          />

          {{ rt(item) }}
        </li>
      </ul>
    </div>

    <div>
      <img
        :src="startEarningImage"
        alt="Shooting star over a camped towable RV"
        class="rounded-2xl h-full object-cover"
      >
    </div>

    <div>
      <NuxtLink
        :to="computedTo"
        class="inline-block bg-white text-primary-950 border border-primary-950 hover:bg-gray-100 focus-visible:outline-primary-950 py-3 px-6 rounded-3xl text-base font-semibold no-underline"
      >
        {{ t('getStarted') }}
      </NuxtLink>
    </div>

    <div />
  </div>
</template>

<script setup lang="ts">
const { t, tm, rt } = useI18n()
const localePath = useLocalePath()
const { userExists } = useUser()

const computedTo = computed(() => {
  if (userExists.value) {
    return localePath({ name: 'dashboard-rvs-new', params: { dashboard: 'host' } })
  }

  return localePath({ name: 'owner' })
})

const { getStaticImage, presets } = useImage()
const startEarningImage = computed(() => getStaticImage({
  path: '/web-app/start-earning.webp',
  preset: presets.LANDSCAPE_MD,
}))
</script>

<i18n lang="json">
  {
    "en": {
      "title": "Start earning with your RV",
      "content": "Thousands of RV owners across Canada and the U.S. earn money by renting out their RV on RVezy.",

      "listYourRv": {
        "title": "List your RV for free today and get:",
        "items": [
          "Up to $150K in RV insurance protection",
          "Up to $2M in liability coverage",
          "24/7 roadside assistance on every rental",
          "Verified, pre-screened guests",
          "1:1 live customer support - with a real human",
          "Access to host tools, add-ons, and calendar availability features",
          "Earnings insights and analytics to help you maximize rental revenue"
        ]
      },

      "getStarted": "Get started"
    },
    "fr": {
      "title": "Commencez à gagner de l’argent avec votre VR",
      "content": "Des milliers de propriétaires de VR au Canada et aux États-Unis gagnent de l’argent en louant leur VR sur RVezy.",

      "listYourRv": {
        "title": "Inscrivez gratuitement votre VR dès aujourd’hui et bénéficiez:",
        "items": [
        "D’une assurance VR pouvant atteindre 150 000$",
        "D’une couverture de responsabilité civile pouvant atteindre 2 million de dollars",
        "D’une assistance routière 24 heures sur 24, 7 jours sur 7 pour chaque location",
        "De voyageurs vérifiés et présélectionnés ",
        "D’un service à la clientèle en direct 1:1, avec un vrai humain",
        "D’un accès à des outils d’hôte, des éléments supplémentaires et des fonctionnalités de calendrier et de disponibilité",
        "D’informations et d’analyses sur vos résultats pour vous aider à maximiser vos revenus de location"
        ]
      },

      "getStarted": "Commencez dès maintenant"
    }
  }
</i18n>
